import type { FC } from 'react';
import { useLocation } from 'react-router-dom';
import Alert from '@components/ui/alert';
import Button from '@components/ui/button';
import ProductCardAlpine from '@components/product/product-cards/product-card-alpine';
import ProductCardLoader from '@components/ui/loaders/product-card-loader';
import cn from 'classnames';
import { LIMITS } from '@framework/utils/limits';
import { Product } from '@framework/types';
import { useTranslation } from '@i18n/client';
import useQueryParam from '@utils/use-query-params';
import { usePaginatedProductsQuery } from '@framework/product/get-products-paginated';
import { InView } from 'react-intersection-observer';
import { useAuthContext } from '@contexts/use-auth-context';

interface ProductGridProps {
  lang: string;
  className?: string;
}

export const ProductGrid: FC<ProductGridProps> = ({ className = '', lang }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation(lang, 'common');
  const { getParams, searchParams, clearQueryParam } = useQueryParam(
    pathname ?? '/',
  );
  const newQuery: any = {
    ...getParams(),
    hide_out_of_stock: getParams().hide_out_of_stock ?? 'false',
  };
  const { isAuthenticated } = useAuthContext();

  const handleButtonChange = (isVisible: boolean) => {
    if (isVisible) {
      fetchNextPage();
    }
  };

  const handleClearFilterClick = () => {
    const keys = Array.from(searchParams.keys());
    clearQueryParam(keys);
  };

  const {
    isFetching: isLoadingProducts,
    isFetchingNextPage: loadingMore,
    isRefetching: isRefechingProducts,
    fetchNextPage,
    hasNextPage,
    data,
    error,
  } = usePaginatedProductsQuery({
    limit: LIMITS.PRODUCTS_LIMITS,
    // @ts-ignore
    newQuery,
  });

  if (
    !isLoadingProducts &&
    !isRefechingProducts &&
    !data?.pages.reduce((acc, curr) => acc + curr.data.length, 0)
  ) {
    return (
      <div className="flex items-center justify-center px-12 py-16 text-center sm:py-20 lg:py-24 xl:py-32">
        <div className="max-w-md xl:max-w-lg">
          <p className="text-24px md:text-base 2xl:text-[18px] leading-7 md:leading-8 pt-4 font-medium pb-7">
            {t('text-no-results-found-for-search')}
          </p>
          <Button
            onClick={handleClearFilterClick}
            className='class="text-[13px] md:text-sm lg:text-[15px] leading-4 inline-flex items-center font-medium cursor-pointer transition ease-in-out duration-300 bg-brand-dark text-white px-4 md:px-6 py-2.5 lg:py-3 hover:text-white bg-opacity-90 hover:bg-opacity-100 rounded-md'
          >
            {t('text-clear-filters')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 gap-3 md:gap-4 2xl:gap-5',
          className,
        )}
      >
        {error ? (
          <div className="col-span-full">
            <Alert message={t('common.error-loading-products')} />
          </div>
        ) : (isRefechingProducts || isLoadingProducts) &&
          !data?.pages?.length ? (
          Array.from({ length: 30 }).map((_, idx) => (
            <ProductCardLoader
              key={`product--key-${idx}`}
              uniqueKey={`product--key-${idx}`}
            />
          ))
        ) : data?.pages?.length ? (
          data?.pages?.map((page: any) => {
            return page?.data?.map((product: Product) => (
              <ProductCardAlpine
                key={`product--key-${product.id}`}
                product={product}
                lang={lang}
              />
            ));
          })
        ) : (
          <div className="col-span-full">
            <Alert message={'no products'} />
          </div>
        )}
      </div>
      {hasNextPage && (
        <div className="pt-8 text-center xl:pt-10">
          <InView
            trackVisibility={true}
            delay={1000}
            onChange={handleButtonChange}
          >
            <Button
              loading={loadingMore}
              disabled={loadingMore}
              onClick={() => fetchNextPage()}
            >
              {t('button-load-more')}
            </Button>
          </InView>
        </div>
      )}
    </>
  );
};
